'use client';

import { useState, useRef, useEffect } from 'react';
import type { MonoIconType } from '@/components/ui/icon';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { getPickerIcon, PICKER_ICON_NAMES } from '@/lib/picker-icons';
import { cn } from '@/lib/utils';
import Loader from '../ui/loader';

const DEFAULT_COLOR = '#64748b';

/** Preset label colors (hex), aligned with common workspace pick-list palettes. */
const COLOR_SWATCHES = [
  '#64748b',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899'
];

type ItemWithColor = { id: string; name: string; color?: string };
type ItemWithIcon = ItemWithColor & { icon?: string };

interface PickListSectionProps {
  title: string;
  description: string;
  items: ItemWithIcon[];
  showIcon: boolean;
  /** Default icon name for new items and when an item has no icon (picker list). */
  defaultIconName?: string;
  /** Placeholder name when adding an item; user edits inline immediately after. */
  defaultNewItemName: string;
  onAdd: (payload: { name: string; color?: string; icon?: string }) => Promise<string | null>;
  onUpdate: (
    id: string,
    payload: { name?: string; color?: string; icon?: string }
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function PickListSection({
  title,
  description,
  items,
  showIcon,
  defaultIconName,
  defaultNewItemName,
  onAdd,
  onUpdate,
  onDelete
}: PickListSectionProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempName, setTempName] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editingId) return;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [editingId]);

  const startEditName = (item: ItemWithIcon) => {
    setEditingId(item.id);
    setTempName(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setTempName('');
  };

  const saveName = async (id: string) => {
    const item = items.find((i) => i.id === id);
    const trimmed = tempName.trim();
    if (!item) {
      cancelEdit();
      return;
    }
    if (!trimmed) {
      cancelEdit();
      return;
    }
    if (trimmed === item.name) {
      cancelEdit();
      return;
    }
    await onUpdate(id, { name: trimmed });
    cancelEdit();
  };

  const onNameKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void saveName(id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleColorChange = (id: string, hex: string) => {
    void onUpdate(id, { color: hex });
  };

  const handleIconChange = (id: string, icon: string) => {
    void onUpdate(id, { icon: icon || undefined });
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const id = await onAdd({
        name: defaultNewItemName,
        color: DEFAULT_COLOR,
        ...(showIcon ? { icon: defaultIconName || 'Circle' } : {})
      });
      if (id) {
        setEditingId(id);
        setTempName(defaultNewItemName);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    await onDelete(deleteId);
    setDeleteId(null);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">{description}</p>
        </div>
        <Button onClick={() => void handleAdd()} disabled={saving}>
          {saving ? <Loader className="h-4 w-4" /> : <MonoIcon type="Plus" />}
          Add {title.slice(0, -1)}
        </Button>
      </div>

      <div className="space-y-1">
        <ul className="space-y-1">
          {items.map((item) => {
            const iconKey = item.icon || defaultIconName || 'Circle';
            const IconComponent = showIcon ? getPickerIcon(iconKey) : null;
            const displayColor = item.color || DEFAULT_COLOR;
            const isEditing = editingId === item.id;

            return (
              <li
                key={item.id}
                className="bg-card border-border group flex items-center justify-between rounded-lg border p-1 shadow-sm"
              >
                <div className="flex min-w-0 flex-1 items-center gap-1">
                  <Popover>
                    <PopoverTrigger
                      className={cn(
                        buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
                        'shrink-0 rounded-lg'
                      )}
                      aria-label={`Color and icon for ${item.name}`}
                    >
                      {IconComponent ? (
                        <MonoIcon
                          type={IconComponent}
                          style={{ color: displayColor }}
                          className="size-5"
                        />
                      ) : (
                        <span
                          className="border-border size-5 shrink-0 rounded-full border"
                          style={{ backgroundColor: displayColor }}
                          aria-hidden
                        />
                      )}
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto max-w-[min(100vw-2rem,20rem)] p-0"
                      align="start"
                    >
                      <div className="flex flex-wrap gap-1 p-1">
                        {COLOR_SWATCHES.map((hex) => (
                          <Button
                            key={hex}
                            variant="ghost"
                            size="icon-sm"
                            className={cn('aspect-square rounded-lg', {
                              'bg-muted': displayColor === hex
                            })}
                            onClick={() => handleColorChange(item.id, hex)}
                            aria-label={`Set color ${hex}`}
                          >
                            <span
                              className="border-border size-2.5 rounded-full border"
                              style={{ backgroundColor: hex }}
                              aria-hidden
                            />
                          </Button>
                        ))}
                      </div>
                      {showIcon && (
                        <>
                          <Separator />
                          <div className="flex flex-wrap gap-1 p-1">
                            {PICKER_ICON_NAMES.map((name) => {
                              const PickerIcon = getPickerIcon(name);
                              const selected = iconKey === name;
                              return (
                                <Button
                                  key={name}
                                  variant="ghost"
                                  size="icon-sm"
                                  className={cn('aspect-square rounded-lg', {
                                    'bg-muted': selected
                                  })}
                                  onClick={() => handleIconChange(item.id, name)}
                                  aria-label={`Icon ${name}`}
                                >
                                  {PickerIcon ? (
                                    <MonoIcon
                                      type={PickerIcon}
                                      style={{ color: displayColor }}
                                      className="size-5"
                                    />
                                  ) : null}
                                </Button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </PopoverContent>
                  </Popover>

                  {isEditing ? (
                    <Input
                      ref={inputRef}
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      onKeyDown={(e) => onNameKeyDown(e, item.id)}
                      onBlur={() => void saveName(item.id)}
                      className="h-8 min-w-0 flex-1 border-0 bg-transparent px-2 text-sm font-medium shadow-none focus-visible:ring-0"
                      placeholder={defaultNewItemName}
                    />
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 min-w-0 flex-1 justify-start truncate px-2 text-sm font-medium"
                      onClick={() => startEditName(item)}
                    >
                      {item.name}
                    </Button>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setDeleteId(item.id)}
                    aria-label={`Delete ${item.name}`}
                  >
                    <MonoIcon type="Trash" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this item?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Tickets using it may show as unset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
